import _ from "lodash";
import React from "react";
import classes from "./DynamicTable.scss";
import I18nHOC from "UIComponents/I18nHOC";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { AddOutlined } from "@toddle-edu/ds-icons";
import {
  createNestedPlannerFields,
  deletePlannerField,
} from "UnitPlans/modules/PlannerFieldThunks";
import { getFieldContextFromParentType } from "UnitPlans/modules/Utils";
import PropTypes from "prop-types";
import DynamicTableRow from "./components/DynamicTableRow";
import { Button, Dialog } from "@toddle-edu/ds-web";
import classNames from "classnames";

const DIALOGS_INFO = {
  DELETE: {
    title: "common:delete_with_label",
    label: "common:row",
    message: "common:delete_msg_with_label",
    confirmButtonText: "common:delete",
    cancelButtonText: "common:cancel",
  },
};

class DynamicTable extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      showLoader: false,
      currentDialogAction: null,
      currentSelectedFieldId: null,
      rowDeletionData: {},
    };
  }

  fieldContext = getFieldContextFromParentType({
    parentType: this.props.fieldContextParentType,
  });

  onClickDelete = ({ fieldId, totalRows, fieldUid }) => {
    this.setState({
      rowDeletionData: { totalRows, fieldUid },
      currentSelectedFieldId: fieldId,
      currentDialogAction: "DELETE",
    });
  };

  createNewChildField = async () => {
    const {
      fieldId,
      parentId,
      parentType,
      createNestedPlannerFields,
    } = this.props;
    this.setState({ showLoader: true });

    await createNestedPlannerFields({
      parentFieldId: fieldId,
      parentId,
      parentType,
      value: "",
    });

    this.setState({ showLoader: false });
  };

  closeDialogue = () => {
    this.setState({ currentDialogAction: null });
  };

  onDeleteRow = () => {
    const { parentId, parentType, deletePlannerField } = this.props;
    const {
      currentSelectedFieldId,
      rowDeletionData: { totalRows, fieldUid } = {},
    } = this.state;

    if (totalRows < 2) {
      this.props.updateInputField({
        [fieldUid]: "",
      });
    } else {
      deletePlannerField({
        fieldId: currentSelectedFieldId,
        parentId,
        parentType,
      });
    }
    this.closeDialogue();
  };

  renderEdit = () => {
    const {
      tableConfig: {
        columnConfig = {},
        columns = [],
        rowConfig = {},
        showDeleteColumn = false,
        showAddButton = false,
        addMaxRowCount = null,
      },
      t,
      tableStyles,
      disabled = false,
      displayDisabledAsActive,
      testId,
    } = this.props;

    const currentDialogAction = this.state.currentDialogAction;

    const FieldContext = this.fieldContext;

    const containerClass = classNames(
      { [classes.container]: true },
      {
        [classes.disableTable]: disabled,
      }
    );

    return (
      <FieldContext.Consumer>
        {({ fields }) => {
          const childrenUidList = _.map(
            this.props.childrenList,
            item => item?.id
          );

          const rows = _.map(childrenUidList, item =>
            _.find(fields, { uid: item })
          );

          const isAddButtonDisabled = this.state.showLoader || disabled;

          const showAddButtonCondition =
            showAddButton &&
            !(!_.isNull(addMaxRowCount) && _.size(rows) >= addMaxRowCount) &&
            !(this.props.displayDisabledAsActive && disabled);

          return (
            <div className={containerClass}>
              <table style={tableStyles}>
                <thead>
                  <tr>
                    {_.map(columns, item => {
                      const columnData = columnConfig[item];
                      return (
                        <th
                          className="text-label"
                          style={columnData?.cellHeaderStyle ?? {}}
                          key={item}
                        >
                          {t(columnData?.label)}
                        </th>
                      );
                    })}
                    {showDeleteColumn && (
                      <th
                        className={classNames(
                          "text-label",
                          classes.deleteColumn
                        )}
                      >
                        {t("common:delete")}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {_.map(rows, (rowData, rowIndex) => (
                    <DynamicTableRow
                      rowData={rowData}
                      columns={columns}
                      columnConfig={columnConfig}
                      rowConfig={rowConfig}
                      updateInputField={this.props.updateInputField}
                      rowIndex={rowIndex}
                      onClickDelete={this.onClickDelete}
                      key={rowData?.id}
                      totalRows={_.size(rows)}
                      showDeleteColumn={showDeleteColumn}
                      disabled={disabled}
                      displayDisabledAsActive={displayDisabledAsActive}
                      testId={testId}
                    />
                  ))}
                </tbody>
              </table>
              {showAddButtonCondition && (
                <div className={classes.bottomComponentContainer}>
                  <span className={classes.createRowButtonContainer}>
                    <Button
                      icon={<AddOutlined variant="link" />}
                      type="inline"
                      variant="progressive"
                      onClick={this.createNewChildField}
                      disabled={
                        isAddButtonDisabled ||
                        (!_.isNull(addMaxRowCount) &&
                          _.size(rows) >= addMaxRowCount)
                      }
                      testId={`${testId}-addRow`}
                    >
                      {t(
                        this.state.showLoader
                          ? "common:adding_with_label"
                          : "common:add_with_label",
                        { label: t("common:row") }
                      )}
                    </Button>
                  </span>
                </div>
              )}
              {currentDialogAction && (
                <Dialog
                  variant="delete-dialog"
                  title={t(DIALOGS_INFO[currentDialogAction].title, {
                    label: t(DIALOGS_INFO[currentDialogAction].label),
                  })}
                  onClose={this.closeDialogue}
                  message={t(DIALOGS_INFO[currentDialogAction].message, {
                    label: t(DIALOGS_INFO[currentDialogAction].label),
                  })}
                  buttonOneText={t(
                    DIALOGS_INFO[currentDialogAction].cancelButtonText
                  )}
                  buttonTwoText={t(
                    DIALOGS_INFO[currentDialogAction].confirmButtonText
                  )}
                  onButtonOneClick={this.closeDialogue}
                  onButtonTwoClick={this.onDeleteRow}
                  dsVersion="2.0"
                  testId={`${testId}-delete`}
                />
              )}
            </div>
          );
        }}
      </FieldContext.Consumer>
    );
  };
}

DynamicTable.propTypes = {
  ...UIBaseComponent.propTypes,
  tableStyles: PropTypes.object,
  tableConfig: PropTypes.object,
  displayDisabledAsActive: PropTypes.bool,
};

DynamicTable.defaultProps = {
  ...UIBaseComponent.defaultProps,
  tableConfig: {},
  tableStyles: {},
  displayDisabledAsActive: false,
};

const mapActionCreators = {
  createNestedPlannerFields,
  deletePlannerField,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(null, mapActionCreators)
)(DynamicTable);
